import { Trophy, MapPin, Calendar, Radio, CalendarPlus, RefreshCw } from "lucide-react";
import { useCompetitions } from "@/hooks/useCompetitions";
import { downloadCompIcs } from "@/lib/ics";
import type { CompetitionStatus } from "@/data/competitions";

const statusStyles: Record<CompetitionStatus, string> = {
  live: "bg-gradient-sunset text-accent-foreground shadow-glow",
  upcoming: "bg-primary/20 text-primary border border-primary/40",
  completed: "bg-muted text-muted-foreground border border-border",
};

const fmt = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

export const CompetitionsList = () => {
  const { competitions: sorted, isLoading, isValidating, refresh } = useCompetitions();

  return (
    <div className="relative rounded-3xl bg-card/80 backdrop-blur-md border border-border shadow-soft p-6">
      {/* Refresh button — top right corner */}
      <button
        onClick={() => refresh()}
        title="Refresh competitions"
        className="absolute top-4 right-4 h-8 w-8 rounded-full flex items-center justify-center hover:bg-primary/10 transition-colors"
      >
        <RefreshCw
          className={`h-4 w-4 text-muted-foreground transition-transform ${
            isLoading || isValidating ? "animate-spin" : ""
          }`}
        />
      </button>

      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="h-10 w-10 rounded-2xl bg-gradient-sunset flex items-center justify-center shadow-glow shrink-0">
          <Trophy className="h-5 w-5 text-accent-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold">2026 Season</div>
          <h2 className="font-display text-xl sm:text-2xl md:text-3xl text-foreground">
            Surf Competitions Worldwide
          </h2>
        </div>
      </div>

      {/* Competition cards */}
      <div className="grid gap-3 md:grid-cols-2">
        {sorted.map((c) => (
          <article
            key={c.id}
            className="rounded-2xl bg-background/60 border border-border p-4 hover:border-primary/40 transition-colors"
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <h3 className="font-display text-lg leading-tight text-foreground">{c.name}</h3>
              <span
                className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full inline-flex items-center gap-1 shrink-0 ${statusStyles[c.status]}`}
              >
                {c.status === "live" && <Radio className="h-3 w-3 animate-pulse" />}
                {c.status}
              </span>
            </div>

            <div className="text-xs text-primary font-semibold uppercase tracking-wider mb-2">
              {c.tour}
            </div>

            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              {c.location}, {c.country}
            </div>

            <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
              <Calendar className="h-3.5 w-3.5 shrink-0" />
              {fmt(c.startDate)} – {fmt(c.endDate)}
            </div>

            <button
              onClick={() => downloadCompIcs(c)}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20 transition-colors"
            >
              <CalendarPlus className="h-3.5 w-3.5" />
              Add to calendar
            </button>
          </article>
        ))}
      </div>
    </div>
  );
};
